import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import moment from 'moment';
import 'bootstrap-daterangepicker/daterangepicker.css';
import countriesData from "../../Config/countries.json";

const MailBoxSidebar = ({
    chats,
    activeChat,
    loadingChats,
    searchQuery,
    setSearchQuery,
    sidebarOpen,
    setSidebarOpen,
    onSelectChat,
    onSyncClick,
    dateRangeText,
    handleApplyDate,
    ranges,
    onUserClick,
    t
}) => {
    const [visibleCount, setVisibleCount] = useState(20);

    // Reset visible count when search query changes
    useEffect(() => {
        setVisibleCount(20);
    }, [searchQuery]);

    const filteredChats = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return chats;
        return chats.filter(chat => {
            const name = chat.name || "";
            const email = chat.email || "";
            const lastMsg = chat.lastMessage?.Title || chat.lastMessage?.Details || "";
            return (
                name.toLowerCase().includes(query) ||
                email.toLowerCase().includes(query) ||
                lastMsg.toLowerCase().includes(query)
            );
        });
    }, [chats, searchQuery]);

    const visibleChats = useMemo(() => {
        return filteredChats.slice(0, visibleCount);
    }, [filteredChats, visibleCount]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setVisibleCount(prev => Math.min(prev + 20, filteredChats.length));
        }
    };

    const getCountryFlag = (countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    };

    return (
        <div className={`chatbot-sidebar-wrapper ${sidebarOpen ? "panel-open" : "panel-closed"}`}>
            {/* Sidebar Rail */}
            <div className="sidebar-rail d-flex flex-column justify-content-between align-items-center py-3">
                <div className="rail-top d-flex flex-column align-items-center w-100">
                    <a href="/" className="rail-logo-wrapper mb-3 d-block">
                        <img
                            src={sidebarOpen ? "/assets/images/logo/logo.png" : "/assets/images/logo/logosmall.png"}
                            alt={t("alarabeyya")}
                            className="rail-logo"
                            style={{ width: sidebarOpen ? '120px' : '32px', transition: 'width 0.3s ease' }}
                        />
                    </a>
                    <button
                        type="button"
                        className="rail-btn hamburger-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        title={sidebarOpen ? t("Pages.Chatbot.CloseMenu") : t("Pages.Chatbot.OpenMenu")}
                    >
                        <i className="fal fa-bars"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.Chatbot.CloseMenu")}</span>}
                    </button>
                    <button
                        type="button"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onSyncClick}
                        title={t("Pages.MailBox.Sync") || "تحديث"}
                    >
                        <i className={loadingChats ? "fal fa-sync fa-spin" : "fal fa-sync"}></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.MailBox.Sync")}</span>}
                    </button>

                    {/* Link to User/Control (Admin Dashboard sub-menu) */}
                    <Link
                        to="/User/Control"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        title={t("Pages.UserControl.title")}
                    >
                        <i className="fal fa-user-cog"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.UserControl.title")}</span>}
                    </Link>
                </div>
            </div>

            {/* Detailed Panel containing Chat/Mail list */}
            {sidebarOpen && (
                <div className="sidebar-panel bg-white border-left h-100" style={{ width: '320px' }}>
                    <div className="panel-tab-content history-tab-content d-flex flex-column h-100">
                        <div className="panel-header d-flex align-items-center justify-content-between p-3 border-bottom">
                            <h3 className="panel-title h6 font-weight-bold m-0 d-flex align-items-center">
                                <i className="fad fa-mail-bulk ml-2" style={{ color: '#92278f', fontSize: '18px' }}></i>
                                <span>{t("Pages.MailBox.title")}</span>
                            </h3>
                        </div>

                        {/* Search and Date Range Picker Controls */}
                        <div className="mailbox-search-container">
                            <div className="mailbox-search-row">
                                <div className="mailbox-date-wrapper">
                                    <i className="fal fa-calendar-alt"></i>
                                    <DateRangePicker
                                        initialSettings={{
                                            opens: 'center',
                                            autoUpdateInput: false,
                                            buttonClasses: 'btn',
                                            cancelClass: "btn-danger",
                                            applyButtonClasses: "btn-primary",
                                            showDropdowns: true,
                                            maxDate: `${moment().format('DD/MM/YYYY')}`,
                                            ranges: ranges,
                                            minYear: 2020,
                                            locale: {
                                                format: 'DD/MM/YYYY',
                                                cancelLabel: t('Close'),
                                                firstDay: 6,
                                                applyLabel: t('Apply'),
                                                customRangeLabel: t('range.customRange'),
                                                monthNames: [
                                                    `${t("months.January")}`,
                                                    `${t("months.February")}`,
                                                    `${t("months.March")}`,
                                                    `${t("months.April")}`,
                                                    `${t("months.May")}`,
                                                    `${t("months.June")}`,
                                                    `${t("months.July")}`,
                                                    `${t("months.August")}`,
                                                    `${t("months.September")}`,
                                                    `${t("months.October")}`,
                                                    `${t("months.November")}`,
                                                    `${t("months.December")}`
                                                ],
                                                daysOfWeek: [
                                                    t("daysOfWeek.Sunday"),
                                                    t("daysOfWeek.Monday"),
                                                    t("daysOfWeek.Tuesday"),
                                                    t("daysOfWeek.Wednesday"),
                                                    t("daysOfWeek.Thursday"),
                                                    t("daysOfWeek.Friday"),
                                                    t("daysOfWeek.Saturday")
                                                ],
                                            },
                                        }}
                                        onApply={handleApplyDate}
                                    >
                                        <input
                                            value={dateRangeText}
                                            readOnly
                                            type="text"
                                            placeholder={t('SearchDate.Placeholder')}
                                            title={t('SearchDate.title')}
                                        />
                                    </DateRangePicker>
                                </div>
                            </div>
                            <div className="mailbox-search-row">
                                <div className="mailbox-query-wrapper">
                                    <i className="fal fa-search"></i>
                                    <input
                                        type="text"
                                        placeholder={t("Pages.MailBox.search")}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            className="panel-scroll-content history-list-scroll flex-grow-1 p-3 overflow-auto"
                            onScroll={handleScroll}
                        >
                            {loadingChats ? (
                                <div className="text-center py-5">
                                    <i className="fa-spin fas fa-spinner text-primary h3"></i>
                                    <p className="mt-2 text-muted">{t("Pages.MailBox.Loading")}</p>
                                </div>
                            ) : filteredChats.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fal fa-envelope h1 text-muted opacity-50 mb-2"></i>
                                    <p>{t("Pages.MailBox.NotFound")}</p>
                                </div>
                            ) : (
                                visibleChats.map(chat => {
                                    const chatName = chat.name || chat.email;
                                    const initials = chatName.substring(0, 1).toUpperCase();
                                    const isActive = activeChat && activeChat.id === chat.id;
                                    const photoUrl = chat.photo;
                                    const countryCode = chat.country;

                                    const lastMsg = chat.lastMessage;
                                    let lastMsgText = "";
                                    if (lastMsg) {
                                        lastMsgText = lastMsg.Title || lastMsg.Details || "";
                                    }

                                    return (
                                        <div
                                            key={chat.id}
                                            className={`history-drawer-item ${isActive ? "active" : ""}`}
                                            onClick={() => onSelectChat(chat)}
                                        >
                                            <div
                                                className="whatsapp-avatar clickable-avatar ml-2"
                                                onClick={(e) => {
                                                    if (chat.user && onUserClick) {
                                                        e.stopPropagation();
                                                        onUserClick(chat.user);
                                                    }
                                                }}
                                                title={chat.user ? t("Pages.WhatsApp.showUserInfo") || "عرض معلومات المستخدم" : ""}
                                            >
                                                {photoUrl ? (
                                                    <LazyLoadImage src={photoUrl} alt={chatName} className="whatsapp-avatar-img" />
                                                ) : (
                                                    initials
                                                )}
                                            </div>
                                            <div className="history-item-content text-right overflow-hidden flex-grow-1">
                                                <div className="history-item-title font-weight-bold text-truncate text-dark d-flex align-items-center flex-wrap gap-1">
                                                    <span className="text-truncate">{chatName}</span>
                                                    {countryCode && (
                                                        <img
                                                            src={process.env.PUBLIC_URL + getCountryFlag(countryCode)}
                                                            className="country-flag-icon"
                                                            title={countryCode.toUpperCase()}
                                                            alt={countryCode.toUpperCase()}
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="history-item-date small text-muted text-truncate mt-1 d-flex justify-content-between align-items-center">
                                                    <span className="text-truncate flex-grow-1 text-right">{lastMsgText}</span>
                                                    {lastMsg && lastMsg.CreationDate && (
                                                        <span className="last-msg-time mr-2" style={{ fontSize: '10px', color: '#94a3b8', flexShrink: 0 }}>
                                                            {lastMsg.CreationDate.split(' ')[0]}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {chat.unreadCount > 0 && (
                                                <span className="mailbox-unread-badge">
                                                    {chat.unreadCount}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MailBoxSidebar;
